import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import { MoreVertical, Shapes } from 'lucide-react';

import { DrawingExamplesDialog } from '../../components/drawings/DrawingExamplesDialog';
import { DrawingsEmptyState } from '../../components/drawings/DrawingsEmptyState';
import { DrawingsSidebar } from '../../components/drawings/DrawingsSidebar';
import { drawingsApi } from '../../components/drawings/drawingsApi';
import { DashboardSearch } from '../../components/projects/DashboardSearch';
import Footer from '../../components/projects/Footer';
import Header from '../../components/projects/Header';
import { NewProjectDialog } from '../../components/projects/NewProjectDialog';
import { useDashboardTabNavigation } from '../../components/projects/useDashboardTabNavigation';
import { useAppDispatch } from '../../hooks';
import { useDrawingsEnabled } from '../../hooks/useDrawingsEnabled';
import { toastReducer } from '../../store/reducers/toast.reducer';
import { DrawingSummary } from '../../types/drawing';

type ViewMode = 'grid' | 'list';
type GridSize = 'small' | 'medium' | 'large';

const CARD_SIZE: Record<GridSize, { width: number; height: number }> = {
  small: { width: 160, height: 120 },
  medium: { width: 200, height: 150 },
  large: { width: 250, height: 187 },
};

/** A remembered view choice (per browser; a blocked storage just means the default). */
function usePreference<T extends string>(key: string, fallback: T): [T, (value: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      return (localStorage.getItem(key) as T | null) ?? fallback;
    } catch {
      return fallback;
    }
  });
  const set = (next: T) => {
    setValue(next);
    try {
      localStorage.setItem(key, next);
    } catch {
      // Not remembered.
    }
  };
  return [value, set];
}

function usePhone() {
  const query = '(max-width: 767px)';
  const [phone, setPhone] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = () => setPhone(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, []);
  return phone;
}

/** The Drawings tab: the user's 2D plans, laid out like Projects; each opens in the polygon editor. */
export function DrawingsPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const enabled = useDrawingsEnabled();
  const handleTabChange = useDashboardTabNavigation('drawings');
  const phone = usePhone();
  const [drawings, setDrawings] = useState<DrawingSummary[] | null>(null);
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [deleting, setDeleting] = useState<DrawingSummary | null>(null);
  // The open actions menu: under the ⋮ button, or at the pointer after a right-click / two-finger click.
  const [menuFor, setMenuFor] = useState<{ id: number; at?: { x: number; y: number } } | null>(null);
  const [query, setQuery] = useState('');
  const [examplesOpen, setExamplesOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [viewMode, setViewMode] = usePreference<ViewMode>('drawings.viewMode', 'grid');
  const [gridSize, setGridSize] = usePreference<GridSize>('drawings.gridSize', 'medium');
  const [sortBy, setSortBy] = usePreference<string>('drawings.sortBy', 'Date modified');

  useEffect(() => {
    if (!enabled) return;
    drawingsApi
      .list()
      .then(setDrawings)
      .catch(() => setDrawings([]));
  }, [enabled]);

  const sorted = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase(i18n.language);
    const list = (drawings ?? []).filter((d) => !needle || d.name.toLocaleLowerCase(i18n.language).includes(needle));
    switch (sortBy) {
      case 'Name A-Z':
        return list.sort((a, b) => a.name.localeCompare(b.name, i18n.language));
      case 'Name Z-A':
        return list.sort((a, b) => b.name.localeCompare(a.name, i18n.language));
      case 'Date created':
        return list.sort((a, b) => b.id - a.id);
      default:
        return list.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    }
  }, [drawings, sortBy, query, i18n.language]);

  if (!enabled) return <Navigate to="/projects" replace />;

  const toast = (message: string) => dispatch(toastReducer.actions.showToast({ message, type: 'error' }));

  // Opens an empty editor; the drawing is stored once something is imported or drawn.
  const createDrawing = () => navigate('/drawings/new');
  // The editor imports the picked file right away (a File survives in the history state).
  const importPlan = (file: File) => navigate('/drawings/new', { state: { importFile: file } });
  const drawSite = () => navigate('/drawings/new?start=draw');

  const rename = async () => {
    if (!renaming) return;
    const name = renaming.name.trim();
    setRenaming(null);
    if (!name) return;
    try {
      await drawingsApi.rename(renaming.id, name);
      setDrawings((current) => current?.map((d) => (d.id === renaming.id ? { ...d, name } : d)) ?? null);
    } catch {
      toast('drawings.renameFailed');
    }
  };

  const remove = async (id: number) => {
    setDeleting(null);
    try {
      await drawingsApi.delete(id);
      setDrawings((current) => current?.filter((d) => d.id !== id) ?? null);
    } catch {
      toast('drawings.deleteFailed');
    }
  };

  const open = (drawing: DrawingSummary) => navigate(`/drawings/${drawing.id}`);
  const formatDate = (value: string) => new Date(value).toLocaleDateString(i18n.language, { dateStyle: 'medium' });
  const meta = (drawing: DrawingSummary) =>
    `${t('drawings.polygonCount', { count: drawing.polygonCount })} · ${formatDate(drawing.updatedAt)}`;

  const nameField = (drawing: DrawingSummary, className: string) =>
    renaming?.id === drawing.id ? (
      <input
        autoFocus
        value={renaming.name}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setRenaming({ id: drawing.id, name: e.target.value })}
        onBlur={rename}
        onKeyDown={(e) => e.key === 'Enter' && rename()}
        maxLength={255}
        className="w-full h-8 rounded bg-black border border-lime-400/50 text-sm px-2 text-white outline-none"
      />
    ) : (
      <h3 className={`text-white font-medium truncate ${className}`} title={drawing.name}>
        {drawing.name}
      </h3>
    );

  const menu = (drawing: DrawingSummary, position: string) => (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setMenuFor(menuFor?.id === drawing.id ? null : { id: drawing.id });
        }}
        aria-label={t('drawings.actions')}
        className={`w-8 h-8 bg-black/70 rounded-full flex items-center justify-center z-20 transition-opacity duration-200 ${position}`}
      >
        <MoreVertical className="w-5 h-5 text-white" />
      </button>
      {menuFor?.id === drawing.id && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={(e) => {
              e.stopPropagation();
              setMenuFor(null);
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuFor(null);
            }}
          />
          <div
            className={`${menuFor.at ? 'fixed' : 'absolute top-11 left-2'} z-40 bg-greyP5 rounded-lg shadow-xl py-2 min-w-[140px]`}
            style={
              menuFor.at
                ? // Kept inside the window near the right and bottom edges.
                  {
                    left: Math.min(menuFor.at.x, window.innerWidth - 160),
                    top: Math.min(menuFor.at.y, window.innerHeight - 150),
                  }
                : undefined
            }
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
          >
            {(
              [
                ['common.open', () => open(drawing)],
                ['drawings.rename', () => setRenaming({ id: drawing.id, name: drawing.name })],
                ['common.delete', () => setDeleting(drawing)],
              ] as const
            ).map(([label, action]) => (
              <button
                key={label}
                onClick={() => {
                  setMenuFor(null);
                  action();
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-greyP4"
              >
                {t(label)}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );

  const openContextMenu = (e: MouseEvent, drawing: DrawingSummary) => {
    e.preventDefault();
    if (renaming?.id === drawing.id) return;
    setMenuFor({ id: drawing.id, at: { x: e.clientX, y: e.clientY } });
  };

  const preview = (drawing: DrawingSummary) =>
    drawing.previewImageUrl ? (
      <img src={drawing.previewImageUrl} alt="" className="w-full h-full object-contain" />
    ) : (
      <Shapes className="w-8 h-8 text-gray-600" />
    );

  const card = phone ? { width: '100%', height: 140 } : CARD_SIZE[gridSize];

  return (
    <div className="family-inter">
      <Header
        activeTab="drawings"
        onTabChange={handleTabChange}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        gridSize={gridSize}
        onGridSizeChange={setGridSize}
        sortBy={sortBy}
        onSortChange={setSortBy}
        onCreateDrawing={createDrawing}
      />

      <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 226px)' }}>
        <DrawingsSidebar onCreate={createDrawing} onOpenExamples={() => setExamplesOpen(true)} />

        <main className="flex-1 overflow-y-auto custom-scrollbar px-4 md:px-8 py-6 text-gray-300">
          {/* The empty state has its own heading. */}
          {drawings?.length !== 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-white text-xl font-medium">{t('drawings.yourDrawings')}</h2>
              {!!drawings?.length && (
                <DashboardSearch value={query} onChange={setQuery} placeholder={t('drawings.search')} />
              )}
            </div>
          )}

          {drawings === null ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : drawings.length === 0 ? (
            <DrawingsEmptyState
              onImport={importPlan}
              onDraw={drawSite}
              onOpenExample={() => setExamplesOpen(true)}
              onCreateProject={() => setNewProjectOpen(true)}
            />
          ) : sorted.length === 0 ? (
            <p className="py-12 text-center text-sm text-gray-500">
              {t('drawings.noMatches', { query: query.trim() })}
            </p>
          ) : viewMode === 'list' ? (
            <div>
              <div className="hidden md:grid grid-cols-[88px_minmax(0,1fr)_140px_160px_40px] gap-4 px-4 py-2 border-b border-greyP4 text-xs text-gray-500">
                <span />
                <span>{t('drawings.columns.name')}</span>
                <span>{t('drawings.columns.polygons')}</span>
                <span>{t('drawings.columns.modified')}</span>
                <span />
              </div>
              {sorted.map((drawing) => (
                <div
                  key={drawing.id}
                  onClick={() => renaming?.id !== drawing.id && open(drawing)}
                  onContextMenu={(e) => openContextMenu(e, drawing)}
                  className="relative grid grid-cols-[72px_minmax(0,1fr)_40px] md:grid-cols-[88px_minmax(0,1fr)_140px_160px_40px] gap-3 md:gap-4 items-center px-2 md:px-4 py-2.5 border-b border-greyP4 hover:bg-greyP5 cursor-pointer"
                >
                  <div className="h-12 md:h-14 rounded bg-black border border-greyP5 overflow-hidden flex items-center justify-center">
                    {preview(drawing)}
                  </div>
                  <div className="min-w-0">
                    {nameField(drawing, 'text-sm')}
                    <p className="md:hidden text-xs text-gray-500 mt-0.5 truncate">{meta(drawing)}</p>
                  </div>
                  <span className="hidden md:block text-sm text-gray-400">{drawing.polygonCount}</span>
                  <span className="hidden md:block text-sm text-gray-400">{formatDate(drawing.updatedAt)}</span>
                  <div className="relative">{menu(drawing, '')}</div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="grid gap-4 md:gap-8"
              style={{
                gridTemplateColumns: phone
                  ? 'repeat(auto-fill, minmax(140px, 1fr))'
                  : `repeat(auto-fill, ${CARD_SIZE[gridSize].width}px)`,
              }}
            >
              {sorted.map((drawing) => (
                <div
                  key={drawing.id}
                  onClick={() => renaming?.id !== drawing.id && open(drawing)}
                  onContextMenu={(e) => openContextMenu(e, drawing)}
                  className="group relative cursor-pointer"
                >
                  {menu(drawing, 'absolute top-2 left-2 opacity-100 md:opacity-0 md:group-hover:opacity-100')}
                  <div
                    className="bg-black rounded-lg overflow-hidden mb-1.5 flex items-center justify-center border-2 border-greyP5 hover:border-greyP6 transition-colors duration-200"
                    style={card}
                  >
                    {preview(drawing)}
                  </div>
                  <div className="space-y-0.5">
                    {nameField(
                      drawing,
                      gridSize === 'small' ? 'text-sm' : gridSize === 'large' ? 'text-xl' : 'text-lg',
                    )}
                    <p className={`text-gray-400 truncate ${gridSize === 'small' ? 'text-xs' : 'text-sm'}`}>
                      {meta(drawing)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      <Footer projectPage={false} />

      {examplesOpen && <DrawingExamplesDialog onClose={() => setExamplesOpen(false)} />}
      {newProjectOpen && <NewProjectDialog open onClose={() => setNewProjectOpen(false)} />}

      {deleting && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setDeleting(null)}
        >
          <div
            className="bg-greyP5 rounded-md shadow-xl w-full max-w-[520px] p-6 md:p-10 text-gray-300"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-white text-lg font-medium pb-2 border-b border-greyP4">{t('drawings.deleteTitle')}</h2>
            <p className="pt-6">{t('drawings.deleteConfirm', { name: deleting.name })}</p>
            <p className="pt-2 text-sm text-gray-400">{t('drawings.deleteHint')}</p>
            <div className="flex items-center justify-end gap-3 pt-8">
              <button
                onClick={() => setDeleting(null)}
                className="px-6 py-2 text-white border border-white rounded-3xl hover:border-gray-500 hover:text-gray-400 transition-colors duration-200"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => remove(deleting.id)}
                className="px-6 py-2 bg-white text-black font-medium rounded-3xl hover:bg-gray-300 transition-colors duration-200"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
